import { useCallback, useState } from 'react'
import {
  COL_MATERIAL_LOTS,
  COL_SLOT_MATERIAL_USAGE,
} from '../../config/collections'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  alert,
  inputClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'
import { pb } from '../../pocketbase'
import type {
  MaterialLotRecord,
  ScheduleSlotRecord,
  SlotMaterialUsageRecord,
} from '../../types/records'
import { Alert } from '../../components/ui/Alert'
import { Button } from '../../components/ui/Button'
import { PageHeader } from '../../components/ui/PageHeader'
import { PageShell } from '../../components/ui/PageShell'

type TraceRow = {
  usageId: string
  lotCode: string
  consumableName: string
  quantityUsed: number
  slotStart: string
  slotEnd: string
  machineLabel: string
  productName: string
  requestClient: string
}

function formatDateRu(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function machineLabel(slot?: ScheduleSlotRecord): string {
  const m = slot?.expand?.machine
  if (!m) return '—'
  return `№${m.number} (${m.type})`
}

function toTraceRows(
  lot: MaterialLotRecord,
  usages: SlotMaterialUsageRecord[],
): TraceRow[] {
  return usages.map((u) => {
    const slot = u.expand?.slot
    const line = slot?.expand?.requestLine
    const product = line?.expand?.product
    const request = line?.expand?.request
    return {
      usageId: u.id,
      lotCode: lot.lotCode,
      consumableName: lot.expand?.consumable?.name ?? '—',
      quantityUsed: u.quantityUsed,
      slotStart: slot?.plannedStart ?? '',
      slotEnd: slot?.plannedEnd ?? '',
      machineLabel: machineLabel(slot),
      productName: product?.name ?? '—',
      requestClient: request?.clientName ?? '—',
    }
  })
}

export function TraceabilityPage() {
  const [query, setQuery] = useState('')
  const [lot, setLot] = useState<MaterialLotRecord | null>(null)
  const [rows, setRows] = useState<TraceRow[]>([])
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)

  const search = useCallback(async () => {
    const code = query.trim()
    if (!code) {
      setErr('Введите код партии сырья.')
      return
    }
    setErr('')
    setLoading(true)
    setLot(null)
    setRows([])
    try {
      const found = await pb.collection(COL_MATERIAL_LOTS).getFirstListItem<MaterialLotRecord>(
        `lotCode="${code.replace(/"/g, '\\"')}"`,
        { expand: 'consumable' },
      )
      setLot(found)

      const usages = await pb
        .collection(COL_SLOT_MATERIAL_USAGE)
        .getFullList<SlotMaterialUsageRecord>({
          filter: `materialLot="${found.id}"`,
          expand:
            'slot,slot.machine,slot.requestLine,slot.requestLine.product,slot.requestLine.request',
          sort: 'created',
        })

      setRows(toTraceRows(found, usages))
    } catch (e) {
      setErr(pocketbaseErrorMessage(e, 'Партия не найдена или нет доступа'))
    } finally {
      setLoading(false)
    }
  }, [query])

  const csvColumns = [
    { header: 'Партия', value: (r: TraceRow) => r.lotCode },
    { header: 'Сырьё', value: (r: TraceRow) => r.consumableName },
    { header: 'Расход', value: (r: TraceRow) => r.quantityUsed },
    { header: 'Слот с', value: (r: TraceRow) => formatDateRu(r.slotStart) },
    { header: 'Слот по', value: (r: TraceRow) => formatDateRu(r.slotEnd) },
    { header: 'Станок', value: (r: TraceRow) => r.machineLabel },
    { header: 'Продукция', value: (r: TraceRow) => r.productName },
    { header: 'Клиент', value: (r: TraceRow) => r.requestClient },
  ]

  return (
    <PageShell>
      <PageHeader
        title="Прослеживаемость партий"
        lead="Цепочка: партия сырья → расход в слоте графика → продукция и заявка клиента."
      />

      <div className={toolbarClass}>
        <input
          id="trace-lot-code"
          type="search"
          className={inputClass}
          style={{ maxWidth: '20rem' }}
          placeholder="Код партии (lotCode)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void search()
          }}
        />
        <Button variant="primary" disabled={loading} onClick={() => void search()}>
          {loading ? 'Поиск…' : 'Найти'}
        </Button>
        {rows.length > 0 ? (
          <ExportCsvButton
            filename={`traceability-${lot?.lotCode ?? 'export'}`}
            rows={rows}
            columns={csvColumns}
          />
        ) : null}
      </div>

      {err ? <Alert kind="err">{err}</Alert> : null}

      {lot ? (
        <div className="mb-4 rounded-su border border-su-border bg-su-surface-muted px-4 py-3 text-sm">
          <strong>{lot.lotCode}</strong>
          {' — '}
          {lot.expand?.consumable?.name ?? '—'}
          {', поступление '}
          {formatDateRu(lot.receivedAt)}
          {', количество '}
          {lot.quantity}
        </div>
      ) : null}

      {lot && rows.length === 0 && !loading && !err ? (
        <p className={alert('warn')}>По этой партии нет записей расхода в слотах графика.</p>
      ) : null}

      {rows.length > 0 ? (
        <div className={tableWrapClass}>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={tableHeadClass}>Расход</th>
                <th className={tableHeadClass}>Слот</th>
                <th className={tableHeadClass}>Станок</th>
                <th className={tableHeadClass}>Продукция</th>
                <th className={tableHeadClass}>Клиент</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.usageId} className={tableRowHoverClass}>
                  <td className={tableCellClass}>{r.quantityUsed}</td>
                  <td className={tableCellClass}>
                    {formatDateRu(r.slotStart)}
                    {' — '}
                    {formatDateRu(r.slotEnd)}
                  </td>
                  <td className={tableCellClass}>{r.machineLabel}</td>
                  <td className={tableCellClass}>{r.productName}</td>
                  <td className={tableCellClass}>{r.requestClient}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </PageShell>
  )
}
